import React, { FC, useCallback, useState } from 'react'
import { Column } from 'react-table'
import { Button, Checkbox, Container, FormControlLabel, Grid, makeStyles, TextField, Typography } from '@material-ui/core'
import Table from '../../components/Table'
import FormDialog from "../../components/dialogs/FormDialog"
import NoDataDialog from '../../components/dialogs/ErrorDialog'
import DividerWithText from '../../components/layout/dividerwithtext'
import moment from "moment"
import "moment/locale/da"
import * as API from '../../api/customermanagement/index'
import * as Models from '../../api/customermanagement/models'
import APIRequest from '../../components/helpers/APIRequest'

moment.locale("da")

const useStyles = makeStyles((theme) => ({
    paper: {
        [theme.breakpoints.up('sm')]: {
            padding: '0',
        },
        marginTop: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    buts: {
        width: '100%',
        height: '100%'
    },
    grid: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(3),
    },
    textHelp: {
        marginBottom: '1rem'
    },
    margTop: {
        marginTop: theme.spacing(2),
    }
}))

const Customers: FC = () => {
    const classes = useStyles()

    const [customers, setCustomers] = useState<Models.CustomersResponse>(new Models.CustomersResponse())
    const [isPendingGetCustomers, setPendingGetCustomers] = useState(false)
    const [runGetCustomers, setRunGetCustomers] = useState(true)

    const [showOnlyActiveCustomers, setShowOnlyActiveCustomers] = useState(true)

    const [showNoDataDialog, setShowNoDataDialog] = React.useState(false)

    // Dialog and related API control
    const [runRequestStoreCustomer, setRunRequestStoreCustomer] = useState(false)
    const [isPendingRequestStoreCustomer, setPendingRunRequestStoreCustomer] = useState(false)
    const [editCustomerOpen, setEditCustomerOpen] = useState(false)

    // Customer definition for the edit dialog
    const [customerDialogTitle, setCustomerDialogTitle] = useState("")
    const [customerId, setCustomerId] = useState<number>(0)
    const [customerName, setCustomerName] = useState("")
    const [customerAddress, setCustomerAddress] = useState("")
    const [customerPhone, setCustomerPhone] = useState("")
    const [customerContact, setCustomerContact] = useState("")
    const [customerComment, setCustomerComment] = useState("")
    const [customerActive, setCustomerActive] = useState(false)
    const [customerPermissionDoorOpener, setCustomerPermissionDoorOpener] = useState(false)
    const [customerPermissionGlobalSearch, setCustomerPermissionGlobalSearch] = useState(false)
    const [customerPermissionInfoModule, setCustomerPermissionInfoModule] = useState(false)
    const [customerPermissionDigitalLicenses, setCustomerPermissionDigitalLicenses] = useState(false)

    const renderPermissionsCell = (customer: Models.Customer) => {
        let c: string[] = []
        if (customer.permissionDoorOpener) { c.push("døråbner") }
        if (customer.permissionGlobalSearch) { c.push("parkantopslag") }
        if (customer.permissionInfoModule) { c.push("infomodul") }
        if (customer.permissionDigitalLicenses) { c.push("digitale licenser") }
        var s = c.join(", ")
        if (s.length > 0) {
            return s.charAt(0).toUpperCase() + s.slice(1)
        }
        // No permissions
        return "-"
    }

    const columns: Column<Models.Customer>[] = React.useMemo(() => [
        {Header: 'ID', id: "0", accessor: 'id'}, 
        {Header: 'Navn', id: "1", accessor: 'navn' }, 
        {Header: 'Adresse', id: "2", accessor: 'adresse'}, 
        {Header: 'Telefon', id: "3", accessor: 'telefon'},
        {Header: 'Kontakt', id: "4", accessor: 'kontakt'}, 
        {Header: 'Kommentar', id: "5", accessor: 'kommentar'}, 
        {Header: 'Aktiv', id: "6", accessor: c => {return c.active ? "Ja" : "-"}},
        {Header: 'Rettigheder', id: "7", accessor: customer => renderPermissionsCell(customer)}], [])

    const handleClickNewCustomer = () => {
        handleRowClick(new Models.Customer())
    }

    const handleRowClick = (element: Models.Customer) => {
        if (element.id > 0) {
            setCustomerDialogTitle(`Ændring af kunde - ID ${element.id}`)
        } else {
            setCustomerDialogTitle("Ny kunde")
        }
        setCustomerId(element.id)
        setCustomerName(element.navn.trim())
        setCustomerAddress(element.adresse)
        setCustomerPhone(element.telefon)
        setCustomerContact(element.kontakt)
        setCustomerComment(element.kommentar)
        setCustomerActive(element.active)
        setCustomerPermissionDoorOpener(element.permissionDoorOpener)
        setCustomerPermissionGlobalSearch(element.permissionGlobalSearch)
        setCustomerPermissionInfoModule(element.permissionInfoModule)
        setCustomerPermissionDigitalLicenses(element.permissionDigitalLicenses)
        setEditCustomerOpen(true)
    }

    const RequestGetCustomers = () => {
        return (<APIRequest<Models.CustomersResponse>
            run={runGetCustomers} setRun={setRunGetCustomers}
            pending={isPendingGetCustomers} setIsPending={setPendingGetCustomers}
            req={() => API.getCustomers()}
            setData={setCustomers}
            setShowError={setShowNoDataDialog}
        />)
    }

    const RequestStoreCustomer = () => {
        return (<APIRequest<Models.CustomersResponse>
            run={runRequestStoreCustomer} setRun={setRunRequestStoreCustomer}
            pending={isPendingRequestStoreCustomer} setIsPending={setPendingRunRequestStoreCustomer}
            req={() => API.storeCustomer(new Models.Customer(customerId, customerName.trim(), customerAddress.trim(), customerPhone.trim(), customerContact.trim(), customerComment.trim(),
                customerActive, customerPermissionDoorOpener, customerPermissionGlobalSearch, customerPermissionInfoModule, customerPermissionDigitalLicenses))}
            setData={setCustomers}
            setShowError={setShowNoDataDialog}
        />)
    }

    const activeFilter = useCallback((customers: Models.Customer[]) => {
        if (showOnlyActiveCustomers) {
            return customers.filter((customer) => customer.active)
        }
        return customers
    }, [showOnlyActiveCustomers])

    return (
        <Container maxWidth={false}>
            <RequestGetCustomers />
            <RequestStoreCustomer />
            <div className={classes.paper}>
                <Typography component="h1" variant="h5">Kunder</Typography>
                <div className={classes.grid}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Button disabled={true} variant="contained" color="primary" className={classes.buts} onClick={handleClickNewCustomer}>Ny kunde</Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel control={<Checkbox name="activeCustomers" id="activeCustomers" checked={showOnlyActiveCustomers} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setShowOnlyActiveCustomers(e.currentTarget.checked)}} />} label="Vis kun aktive kunder" />
                        </Grid>
                        <Grid item xs={12}>
                            <Table<Models.Customer> columns={columns} data={activeFilter(customers.result)} initialState={{pageSize: 150}} clickEvent={handleRowClick} update={null} pending={false} text={""}/>
                        </Grid>
                    </Grid>
                </div>
            </div>

            <NoDataDialog open={showNoDataDialog} onClose={() => {setShowNoDataDialog(false)}} title={"Intet svar"} text={"Noget gik galt, prøv venligst igen senere."}/>

            <FormDialog title={customerDialogTitle} open={editCustomerOpen} onCloseCancel={() => setEditCustomerOpen(false)} onCloseSubmit={() => { setRunRequestStoreCustomer(true); setEditCustomerOpen(false) }}>
                <Grid container spacing={2} style={{padding: 8}}>
                    <Grid item xs={12} sm={6}>
                        <TextField autoComplete="name" name="name" variant="outlined" fullWidth id="name" label="Kundenavn" value={customerName} autoFocus onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setCustomerName(e.currentTarget.value);}} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField autoComplete="address" name="address" variant="outlined" fullWidth id="address" label="Adresse" value={customerAddress} autoFocus onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setCustomerAddress(e.currentTarget.value);}} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField autoComplete="phone" name="phone" variant="outlined" fullWidth id="phone" label="Telefon" value={customerPhone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setCustomerPhone(e.currentTarget.value);}} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField autoComplete="contact" name="contact" variant="outlined" fullWidth id="contact" label="Kontakt" value={customerContact} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setCustomerContact(e.currentTarget.value);}} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                    <FormControlLabel
                        control={<Checkbox name="active" id="active" checked={customerActive} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setCustomerActive(e.currentTarget.checked)}} />}
                        label="Aktiv" />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <TextField autoComplete="comment" name="comment" variant="outlined" fullWidth id="comment" label="Kommentar" value={customerComment} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setCustomerComment(e.currentTarget.value);}} />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <DividerWithText>Rettigheder</DividerWithText>
                        <p>Basis: Alarmer, MobilBase, Statistik og Brugerhåndtering.</p>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Checkbox name="permission_dooropener" id="permission_dooropener" label="Døråbner" checked={customerPermissionDoorOpener} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setCustomerPermissionDoorOpener(e.currentTarget.checked)}} />}
                            label="SMS dør åbner" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Checkbox name="permission_globalSearch" id="permission_globalSearch" label="Parkantopslag" checked={customerPermissionGlobalSearch} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setCustomerPermissionGlobalSearch(e.currentTarget.checked)}} />}
                            label="Parkantopslag" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Checkbox name="permission_infomodul" id="permission_infomodul" label="InfoModul" checked={customerPermissionInfoModule} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setCustomerPermissionInfoModule(e.currentTarget.checked)}} />}
                            label="InfoModul" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Checkbox name="permission_digitalLicenses" id="permission_digitalLicenses" label="Digitale licenser" checked={customerPermissionDigitalLicenses} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setCustomerPermissionDigitalLicenses(e.currentTarget.checked)}} />}
                            label="Digitale licenser" />
                    </Grid>
                </Grid>
            </FormDialog>
        </Container>
    )
}

export default Customers
